import React, { SetStateAction, useCallback, useEffect, useState } from "react";
import {
  Box,
  CardContent,
  FormControl,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  NativeSelect,
  Tooltip,
  Typography,
  makeStyles,
  IconButton,
  colors,
} from "@material-ui/core";
import {
  green as myGreen,
  red as myRed,
  grey as myGrey,
  yellow as myYellow,
  red as myred,
} from "@material-ui/core/colors";
import {
  Person as PersonIcon,
  PersonOutline,
  FiberManualRecord as Circle,
  ExpandMore,
  ExpandLess,
  PhoneCallback,
  PanTool,
  StarSharp,
  Notifications,
} from "@material-ui/icons";
import MeepleHeadset from "./icons/MeepleHeadset";
import { CXAgent, CXExtension } from "./CxDashboard";

const green = {
  ...myGreen,
  1000: "rgba(144, 238, 144, 0.2)",
};

const red = {
  ...myRed,
  1000: "rgba(255, 204, 203, 0.2)",
};

const grey = {
  ...myGrey,
  1000: "rgb(155 155 155 / 20%)",
};

function regexCheck(str: string, number: string): boolean {
  const escapeRegex = (s: string): string =>
    s.replace(/([.+^=!:${}()|\\])/g, "\\$1");

  const temp = "^".concat(number.split("*").map(escapeRegex).join(".*"), "$");
  const r = new RegExp(temp.split("?").join("."));

  return r.test(str);
}

function Time({
  time,
  agent,
  formatDuration,
  formatApproxDuration,
  formatDailyDuration,
  elapsedTime,
  offset,
  liveCall,
}: {
  time: string | undefined;
  agent: CXAgent;
  formatDuration: (t: number) => string;
  formatApproxDuration: (t: number) => string;
  formatDailyDuration: (t: number) => string;
  elapsedTime: string;
  offset: number;
  liveCall: boolean;
}) {
  const [duration, setDuration] = useState<string | number | undefined>(
    "(00:00)"
  );

  useEffect(() => {
    const updateTimer = () => {
      if (time) {
        let agentTime;
        let timeNow;
        let localOffset;
        let utcTimeNow: number;
        let diff: number;
        if (liveCall) {
          agentTime = new Date(time).getTime();
          timeNow = new Date().getTime();
          localOffset = new Date().getTimezoneOffset() * 60000;
          utcTimeNow = timeNow - localOffset;
          diff = utcTimeNow - agentTime + offset;
        }
        if (!liveCall) {
          agentTime = new Date(time).getTime();
          timeNow = new Date().getTime();
          diff = timeNow + offset - agentTime;
        }

        if (elapsedTime === "full") {
          setDuration(`(${formatDuration(Math.round(diff! / 1000))})`);
        }
        if (elapsedTime === "approximate") {
          setDuration(`(${formatApproxDuration(Math.round(diff!))})`);
        }
        if (elapsedTime === "daily") {
          setDuration(`(${formatDailyDuration(Math.round(diff! / 1000))})`);
        }
      }
    };
    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [agent, elapsedTime]);

  return <span style={{ fontWeight: "bold" }}>{duration}</span>;
}

function CxAgent({
  mode,
  agent,
  sidebar,
  rcId,
  origin,
  i,
  color,
  backgroundWeight,
  classes,
  colorWeight,
  formatDuration,
  formatApproxDuration,
  formatDailyDuration,
  elapsedTime,
  offset,
  extension,
  organizationId,
  isAdmin,
  apiUrl,
  historicalUsers,
  handIsRaised,
  displayPortraits,
  vipList,
  setProfileId,
  setPage,
  adminclick,
}: {
  mode: string;
  agent: CXAgent;
  origin: string,
  sidebar: boolean;
  rcId: string;
  i: number;
  backgroundWeight: 100 | 1000;
  color: string;
  classes: any;
  colorWeight: 600 | 300;
  formatDuration: (time: number) => string;
  formatApproxDuration: (time: number) => string;
  formatDailyDuration: (time: number) => string;
  elapsedTime: string;
  offset: number;
  extension: CXExtension | undefined;
  organizationId: string;
  isAdmin: boolean;
  apiUrl: string;
  historicalUsers: any;
  handIsRaised: boolean;
  displayPortraits: boolean;
  vipList: any;
  setProfileId: React.Dispatch<SetStateAction<any>>
  setPage: React.Dispatch<SetStateAction<any>>
  adminclick: (id: string) => void
}) {
  const [currentCall, setCurrentCall] = useState<string | undefined>();

  const clickOnHand = useCallback(
    async (userId: string, handRaised: boolean) => {
      if (
        userId !== undefined &&
        organizationId !== undefined &&
        handRaised !== undefined &&
        (isAdmin || userId === rcId)
      ) {
        const response = await fetch(apiUrl, {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            event: `/restapi/v1.0/account/${organizationId}/extension/${userId}/presence`,
            body: {
              emergency: !handRaised,
            },
          }),
        });
      }
    },
    [rcId, organizationId, apiUrl]
  );
  return (
    <Grid
      item
      key={agent.agentId}
      xs={12}
      sm={sidebar ? 12 : 6}
      md={sidebar ? 6 : 4}
      lg={sidebar ? 4 : 3}
      style={{
        backgroundColor: "#00000000",
        borderRadius: 9,
      }}
    >
      <Box
        onClick={() => {
          if (agent.rcUserId) {
            adminclick(agent.rcUserId)
          }
        }}
        className={`user-card-container cx-card-parent ${mode === 'light' ? "light" : "dark"}`}
        style={{
          borderRadius: 8,
          cursor: 'pointer',
          animationDelay: `${i * 10}ms`,
          outline: (() => {
            if (agent.rcUserId) {
              if (agent.rcUserId === rcId) {
                if (agent.agentState === "AVAILABLE") {
                  return "3px solid green";
                }
                if (
                  agent.agentState === "ENGAGED" ||
                  agent.agentState === "BREAK-AFTER-CALL" ||
                  agent.agentState === "TRANSITION" ||
                  agent.agentState === "PENDING-DISPOSITION" ||
                  agent.agentState === "RNA-STATE" ||
                  agent.agentState === "TRAINING" ||
                  agent.agentState === "WORKING" ||
                  agent.agentState === "ON-BREAK" ||
                  agent.agentState === "AWAY" ||
                  agent.agentState === "LUNCH"
                ) {
                  return "3px solid red";
                }
                if (
                  agent.agentState === "OFF-LINE" ||
                  agent.agentState === "Unknown" ||
                  agent.agentState === "UNKNOWN"
                ) {
                  return "3px solid gray";
                }
              }
            }
            return "0";
          })(),
          outlineOffset: (() => {
            if (agent.rcUserId === rcId) {
              return -3;
            }
            return 0;
          })(),
          backgroundColor: (() => {
            if (agent.agentState === "AVAILABLE") {
              return green[backgroundWeight];
            }
            if (
              agent.agentState === "ENGAGED" ||
              agent.agentState === "BREAK-AFTER-CALL" ||
              agent.agentState === "TRANSITION" ||
              agent.agentState === "PENDING-DISPOSITION"
            ) {
              return red[backgroundWeight];
            }
            if (
              agent.agentState === "TRAINING" ||
              agent.agentState === "WORKING" ||
              agent.agentState === "ON-BREAK" ||
              agent.agentState === "AWAY" ||
              agent.agentState === "LUNCH" ||
              agent.agentState === "RNA-STATE"
            ) {
              return red[backgroundWeight];
            }
            if (
              agent.agentState === "OFF-LINE" ||
              agent.agentState === "Unknown" ||
              agent.agentState === "UNKNOWN"
            ) {
              return grey[backgroundWeight];
            }
            return grey[backgroundWeight];
          })(),

          color,
          display: "flex",
          alignItems: "center",
          height: 97,
        }}
      >
        {displayPortraits && (
          <div
            className={classes.pos}
            style={{
              width: 60,
              alignItems: "flex-start",
            }}
          >
            <img
              aria-label="portrait"
              src={agent.portrait || "./default-user-image.ico"}
              alt="Profile"
              style={{
                borderRadius: "50%",
                height: 50,
                width: 50,
                paddingLeft: 6,
                paddingRight: 4,
              }}
            />
          </div>
        )}

        <CardContent
          style={{
            width: `calc(100% ${displayPortraits ? "- 75px" : "+ 50px"})`,
            display: "flex",
            alignItems: "center",
            padding: 6,
          }}
        >
          <Typography
            className={classes.pos}
            component="div"
            style={{
              width: `calc(100% ${displayPortraits ? "- 50px" : "+ 50px"})`,
              minWidth: 0,
              alignItems: "flex-start",
            }}
          >
            <div style={{ fontWeight: "bold", fontSize: "0.85rem" }}>
              {agent.firstName} {agent.lastName}
            </div>
            <div style={{display: 'flex', width: '100%'}}>
              <div style={{flex: '1 1 100%', minWidth: 0}}>
              <div style={{ fontSize: "0.7rem", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.global || " "}</div>
              </div>

            </div>
            {agent && agent.agentState && agent.agentState === "AVAILABLE" && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <Circle
                  style={{
                    color: "green",
                    fontSize: "0.8rem",
                    marginRight: 3,
                  }}
                  aria-label="Available icon"
                  aria-hidden={false}
                  role="img"
                />
                <span style={{ marginRight: 3, fontSize: "0.7rem" }}>
                  {agent.agentState}
                  <Time
                    agent={agent}
                    time={agent?.stateTimestamp}
                    formatApproxDuration={formatApproxDuration}
                    formatDuration={formatDuration}
                    formatDailyDuration={formatDailyDuration}
                    elapsedTime={elapsedTime}
                    offset={offset}
                    liveCall={false}
                  />
                </span>
              </div>
            )}
            {agent &&
              agent.agentState &&
              (agent.agentState === "ENGAGED" ||
                agent.agentState === "TRANSITION" ||
                agent.agentState === "BREAK-AFTER-CALL" ||
                agent.agentState === "PENDING-DISPOSITION") && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Circle
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginRight: 3,
                    }}
                    aria-label="Available icon"
                    aria-hidden={false}
                    role="img"
                  />
                  <span style={{ marginRight: 3, fontSize: "0.7rem" }}>
                    {agent.agentState}{" "}
                    <Time
                      agent={agent}
                      time={agent?.stateTimestamp}
                      formatApproxDuration={formatApproxDuration}
                      formatDuration={formatDuration}
                      formatDailyDuration={formatDailyDuration}
                      elapsedTime={elapsedTime}
                      offset={offset}
                      liveCall={false}
                    />
                  </span>
                </div>
              )}
            {agent &&
              agent.agentState &&
              (agent.agentState === "OFF-LINE" ||
                agent.agentState === "UNKNOWN" ||
                agent.agentState === "ON-BREAK" ||
                agent.agentState === "AWAY" ||
                agent.agentState === "LUNCH" ||
                agent.agentState === "Unknown") && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Circle
                    style={{
                      color: "grey",
                      fontSize: "0.8rem",
                      marginRight: 3,
                    }}
                    aria-label="Working icon"
                    aria-hidden={false}
                    role="img"
                  />
                  <span style={{ marginRight: 3, fontSize: "0.7rem" }}>
                    {agent.agentState}{" "}
                    <Time
                      agent={agent}
                      time={agent?.stateTimestamp}
                      formatApproxDuration={formatApproxDuration}
                      formatDuration={formatDuration}
                      formatDailyDuration={formatDailyDuration}
                      elapsedTime={elapsedTime}
                      offset={offset}
                      liveCall={false}
                    />
                  </span>
                </div>
              )}
            {agent &&
              agent.agentState &&
              (
                agent.agentState === "WORKING" ||
                agent.agentState === "TRAINING"
                ) && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Circle
                    style={{
                      color: "#f7b500",
                      fontSize: "0.8rem",
                      marginRight: 3,
                    }}
                    aria-label="Working icon"
                    aria-hidden={false}
                    role="img"
                  />
                  <span style={{ marginRight: 3, fontSize: "0.7rem" }}>
                    {agent.agentState}{" "}
                    <Time
                      agent={agent}
                      time={agent?.stateTimestamp}
                      formatApproxDuration={formatApproxDuration}
                      formatDuration={formatDuration}
                      formatDailyDuration={formatDailyDuration}
                      elapsedTime={elapsedTime}
                      offset={offset}
                      liveCall={false}
                    />
                  </span>
                </div>
              )}
              {agent && agent.agentState && agent.agentState === "RNA-STATE" && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Circle
                    style={{
                      color: "red",
                      fontSize: "0.8rem",
                      marginRight: 3,
                    }}
                    aria-label="Working icon"
                    aria-hidden={false}
                    role="img"
                  />
                  <span style={{ marginRight: 3, fontSize: "0.7rem" }}>
                    {agent.agentState}{" "}
                    <Time
                      agent={agent}
                      time={agent?.stateTimestamp}
                      formatApproxDuration={formatApproxDuration}
                      formatDuration={formatDuration}
                      formatDailyDuration={formatDailyDuration}
                      elapsedTime={elapsedTime}
                      offset={offset}
                      liveCall={false}
                    />
                  </span>
                </div>
              )}
            {agent && agent.agentState === undefined && (
              <div style={{ display: "flex", alignItems: "center" }}>
                {/* <Circle
                      style={{
                        color: "grey",
                        fontSize: "0.8rem",
                        marginRight: 3,
                      }}
                      aria-label="Invisible icon"
                      aria-hidden={false}
                      role="img"
                    />
                    <span style={{ marginRight: 3,  fontSize: '0.7rem'  }}>Unavailable</span> */}
              </div>
            )}
            {agent && agent.ringingCalls && (
              <div style={{ display: "flex", alignItems: "center" }}>
                {agent.ringingCalls.map((phone) => (
                  <span
                    style={{
                      marginRight: 3,
                      fontSize: "0.7rem",
                      fontWeight: "bold",
                    }}
                  >
                    <span className="ringing-bell">
                      <Notifications style={{ fontSize: "0.85rem" }} />
                    </span>
                    <span style={{ fontWeight: "bolder" }}>
                      +{phone.phoneNumber}
                    </span>
                    {vipList?.map((vip: any) => {
                      if (regexCheck(phone.phoneNumber || "", vip.number)) {
                        return (
                          <>
                            <StarSharp
                              style={{
                                color: "#DAA520",
                                height: "12px",
                                width: "12px",
                              }}
                            />
                            {vip.name}
                          </>
                        );
                      }
                      return null;
                    })}
                     {" for "} 
                    <Time
                      agent={agent}
                      time={phone.timestamp}
                      formatApproxDuration={formatApproxDuration}
                      formatDuration={formatDuration}
                      formatDailyDuration={formatDailyDuration}
                      elapsedTime={elapsedTime}
                      offset={offset}
                      liveCall
                    />
                  </span>
                ))}
              </div>
            )}
            {agent && agent.activeCalls && (
              <div style={{ display: "flex", alignItems: "center" }}>
                {agent.activeCalls.map((phone) => (
                  <span
                    style={{
                      marginRight: 3,
                      fontSize: "0.7rem",
                      fontWeight: "bold",
                    }}
                  >
                    <PhoneCallback
                      style={{
                        fontSize: "0.85rem",
                        float: "left",
                        fontWeight: "bold",
                        marginRight: 4,
                      }}
                    />
                    <span style={{ fontWeight: "bolder" }}>
                      +{phone.phoneNumber}
                    </span>
                    {vipList?.map((vip: any) => {
                      if (regexCheck(phone.phoneNumber || "", vip.number)) {
                        return (
                          < >
                            <StarSharp
                              style={{
                                color: "#DAA520",
                                height: "12px",
                                width: "12px",
                              }}
                            />
                            {vip.name}
                          </ >
                        );
                      }
                      return null;
                    })}
                     {" for "} 
                    <Time
                      agent={agent}
                      time={phone.timestamp}
                      formatApproxDuration={formatApproxDuration}
                      formatDuration={formatDuration}
                      formatDailyDuration={formatDailyDuration}
                      elapsedTime={elapsedTime}
                      offset={offset}
                      liveCall
                    />
                    {/* <span style={{ marginRight: 3, fontSize: "0.7rem" }}>
                      {formatDuration(
                        Math.round(
                          (new Date().getTime() -
                            new Date(
                              phone.timestamp
                                .replace("T", " ")
                                .replace(".000Z", "")
                            ).getTime()) /
                            1000
                        )
                      )}
                    </span> */}
                  </span>
                ))}
              </div>
            )}
          </Typography>
          <Typography
            className={classes.pos}
            style={{
              width: "auto",
              alignItems: "flex-end",
              maxHeight: 53,
              display: "flex",
              flexDirection: "column",
            }}
            component="div"
          >
            <Typography
              className={classes.pos}
              component="div"
              style={{
                width: 20,
                alignItems: "flex-start",
                height: 16,
              }}
            >
              {rcId === agent.rcUserId && (
                <div
                  role="button"
                  aria-hidden="true"
                  tabIndex={0}
                  style={{
                    color: handIsRaised ? red[500] : "black",
                    padding: 0,
                  }}
                  className="MuiButtonBase-root MuiIconButton-root"
                  onClick={(event) => {
                    event.stopPropagation();
                    clickOnHand(rcId, handIsRaised);
                  }}
                >
                  <span className="MuiIconButton-label">
                    <Tooltip title={handIsRaised ? "Lower Hand" : "Raise Hand"}>
                      <PanTool
                        className={
                          handIsRaised ? "hand-icon-active" : "hand-icon"
                        }
                        style={{ fontSize: "0.85rem" }}
                      />
                    </Tooltip>
                  </span>
                </div>
              )}
              {/* not your hand, is admin */}
              {rcId !== agent.rcUserId && isAdmin && handIsRaised && (
                <div
                  role="button"
                  aria-hidden="true"
                  tabIndex={0}
                  style={{
                    color: handIsRaised ? red[500] : "black",
                    padding: 0,
                  }}
                  className="MuiButtonBase-root MuiIconButton-root"
                  onClick={(event) => {
                    event.stopPropagation();
                    clickOnHand(rcId, handIsRaised);
                  }}
                >
                  <span className="MuiIconButton-label">
                    <Tooltip title={handIsRaised ? "Lower Hand" : "Raise Hand"}>
                      <PanTool
                        className={
                          handIsRaised ? "hand-icon-active" : "hand-icon"
                        }
                        style={{ fontSize: "0.85rem" }}
                      />
                    </Tooltip>
                  </span>
                </div>
              )}
              {/* not your hand, view only (non admins) */}
              {rcId !== agent.rcUserId && !isAdmin && handIsRaised && (
                <div
                  role="button"
                  aria-hidden="true"
                  tabIndex={0}
                  style={{
                    color: handIsRaised ? red[500] : "black",
                    padding: 0,
                    cursor: "default",
                  }}
                  className="MuiButtonBase-root MuiIconButton-root"
                >
                  <span className="MuiIconButton-label">
                    <Tooltip title="Hand Raised">
                      <PanTool
                        className={
                          handIsRaised ? "hand-icon-active" : "hand-icon"
                        }
                        style={{ fontSize: "0.85rem" }}
                      />
                    </Tooltip>
                  </span>
                </div>
              )}
              <span />
            </Typography>
            {agent.groupName && (
              <Tooltip title={agent.groupName}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    width: 90,
                    maxWidth: 90,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "end",
                  }}
                >
                  {agent.groupName && agent.groupName}
                </div>
              </Tooltip>
            )}

            {/* available meeples */}
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
                width: "100%",
                maxHeight: 20,
                marginTop: 4,
              }}
            >
              {agent.agentType !== undefined &&
                agent.agentType === "SUPERVISOR" && (
                  <div style={{ height: 20 }}>
                    <Tooltip title="Supervisor" arrow style={{ margin: 0 }}>
                      <div style={{ height: 20 }}>
                        <MeepleHeadset
                          style={{
                            color: "#d5a640",
                            width: 17,
                            height: 17,
                            alignSelf: "end",
                            paddingRight: 3,
                          }}
                          role="img"
                        />
                      </div>
                    </Tooltip>
                  </div>
                )}
              {(agent.agentState === "AVAILABLE" ||
                agent.agentState === "OFF-LINE") &&
                agent.queues &&
                agent.queues.filter((queue) => queue.login === true).length >
                  0 && (
                  <Tooltip
                    arrow
                    title={
                      <div>
                        {agent.queues
                          .filter((queue) => queue.login === true)
                          .map((queue) => (
                            <div key={queue.queueId}>{queue.queueName}</div>
                          ))}
                      </div>
                    }
                  >
                    <PersonIcon
                      aria-hidden={false}
                      role="img"
                      style={{
                        color: green[colorWeight],
                        width: 24,
                        height: 24,
                        marginTop: 5,
                        alignSelf: "end",
                      }}
                    />
                  </Tooltip>
                )}
              {(agent.agentState === "AVAILABLE" ||
                agent.agentState === "OFF-LINE") &&
                agent.queues &&
                agent.queues.filter((queue) => queue.login === false).length >
                  0 && (
                  <Tooltip
                    arrow
                    title={
                      <div>
                        {agent.queues
                          .filter((queue) => queue.login === false)
                          .map((queue) => (
                            <div key={queue.queueId}>{queue.queueName}</div>
                          ))}
                      </div>
                    }
                  >
                    <PersonOutline
                      aria-hidden={false}
                      role="img"
                      style={{
                        color: green[colorWeight],
                        width: 24,
                        height: 24,
                        marginTop: 5,
                        alignSelf: "end",
                      }}
                    />
                  </Tooltip>
                )}
              {/* unavailable meeples */}
              {agent.agentState !== "AVAILABLE" &&
                agent.agentState !== "OFF-LINE" &&
                agent.queues &&
                agent.queues.filter((queue) => queue.login === true).length >
                  0 && (
                  <Tooltip
                    arrow
                    title={
                      <div>
                        {agent.queues
                          .filter((queue) => queue.login === true)
                          .map((queue) => (
                            <div key={queue.queueId}>{queue.queueName}</div>
                          ))}
                      </div>
                    }
                  >
                    <PersonIcon
                      aria-hidden={false}
                      role="img"
                      style={{
                        color: red[colorWeight],
                        width: 24,
                        height: 24,
                        marginTop: 5,
                        alignSelf: "end",
                      }}
                    />
                  </Tooltip>
                )}
              {agent.agentState !== "AVAILABLE" &&
                agent.agentState !== "OFF-LINE" &&
                agent.queues &&
                agent.queues.filter((queue) => queue.login === false).length >
                  0 && (
                  <Tooltip
                    arrow
                    title={
                      <div>
                        {agent.queues
                          .filter((queue) => queue.login === false)
                          .map((queue) => (
                            <div key={queue.queueId}>{queue.queueName}</div>
                          ))}
                      </div>
                    }
                  >
                    <PersonOutline
                      aria-hidden={false}
                      role="img"
                      style={{
                        color: red[colorWeight],
                        width: 24,
                        height: 24,
                        marginTop: 5,
                        alignSelf: "end",
                      }}
                    />
                  </Tooltip>
                )}
            </div>
          </Typography>
        </CardContent>
      </Box>
    </Grid>
  );
}

export default CxAgent;
